'use client';
// L'onglet « Cartes archivées » : les lots refermés de toutes les cartes, à rouvrir en lecture (RQ, 03/10/2026).
import React from 'react';
import { fmtDate } from '@/lib/meta';

import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';
export interface Archive {
  periodeId: string; carteId: string;
  equipement: { code: string; nom: string } | null; analyte: { code: string; nom: string } | null;
  lot: string | null; dateDebut: string | null; dateFin: string | null; datePeremption: string | null;
  points: number; derniere: string | null; carteActive: boolean;
}

export function CqiArchives({ archives, erreur, onReessayer, onOuvrir }: {
  archives: Archive[] | null; erreur: boolean; onReessayer: () => void; onOuvrir: (a: Archive) => void;
}) {
  /* Le nom de l'automate derrière son code : le filtre « Automate » du socle
     ne connaît que la valeur brute (le code), et le menu doit dire « EQ03 · Cobas ». */
  const nomsEquipements = React.useMemo(
    () => new Map((archives || []).filter((a) => a.equipement).map((a) => [a.equipement!.code, a.equipement!.nom])),
    [archives],
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Les lots refermés : un nouveau lot de contrôle archive l’ancien, et une carte archivée sort des cartes en cours.
        Rien n’est supprimé — chaque lot se rouvre en lecture seule, avec ses points et son journal.
      </p>
      {/* L7 vague 2 (11/10/2026) : registre STANDARD sur le socle. La recherche et
          le filtre « Automate » (liste) sont ceux du tableau — plus de champ ni de
          sélecteur à côté ; états intégrés (squelette, erreur + Réessayer, vide) ;
          « Ouvrir » en colonne d'actions. */}
      <Tableau
        id="cqi-archives"
        ajusterLargeur
        niveau="standard"
        recherche="Paramètre, automate, lot…"
        chargement={!archives && !erreur}
        erreur={erreur ? 'Les cartes archivées n’ont pas pu être lues. Cet écran ne dit rien sur l’existence de lots refermés tant que la lecture n’a pas abouti.' : null}
        onReessayer={onReessayer}
        vide={{ titre: 'Aucune carte archivée.', indice: 'Un lot refermé avec des points apparaît ici.' }}
        lignes={archives || []}
        cleLigne={(a) => a.periodeId}
        colonnes={[
          {
            cle: 'equipement', libelle: 'Automate', type: 'liste',
            valeur: (a) => a.equipement?.code || '',
            libelleValeur: (code) => (nomsEquipements.get(code) ? `${code} · ${nomsEquipements.get(code)}` : code || '—'),
            rendu: (a) => <>{a.equipement?.code} <span style={{ color: 'var(--text-muted)' }}>· {a.equipement?.nom}</span></>,
          },
          {
            cle: 'analyte', libelle: 'Paramètre',
            valeur: (a) => `${a.analyte?.code || ''} ${a.analyte?.nom || ''}`.trim(),
            rendu: (a) => (
              <>
                <strong>{a.analyte?.code}</strong> <span style={{ color: 'var(--text-muted)' }}>{a.analyte?.nom}</span>
                {!a.carteActive && <span style={{ marginInlineStart: 8, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-pill)', padding: '1px 8px' }}>carte archivée</span>}
              </>
            ),
          },
          { cle: 'lot', libelle: 'Lot de contrôle', valeur: (a) => a.lot || '', style: { fontFamily: 'var(--font-mono)' }, rendu: (a) => a.lot || '—' },
          { cle: 'debut', libelle: 'Début', type: 'date', valeur: (a) => a.dateDebut || '', rendu: (a) => (a.dateDebut ? fmtDate(a.dateDebut) : '…') },
          {
            cle: 'fin', libelle: 'Fin', type: 'date', valeur: (a) => a.dateFin || a.derniere || '',
            /* Un lot encore sans date de fin se lit par son DERNIER point : c'est la date utile. */
            rendu: (a) => (a.dateFin ? fmtDate(a.dateFin) : a.derniere ? fmtDate(a.derniere) : '—'),
          },
          { cle: 'points', libelle: 'Points', type: 'nombre', valeur: (a) => a.points, rendu: (a) => a.points },
        ]}
        actions={(a) => <Bouton taille="sm" type="button" onClick={() => onOuvrir(a)}>Ouvrir</Bouton>}
      />
    </div>
  );
}
